import React, { ReactNode, useEffect, useState } from 'react';
import { useMediaQuery } from 'usehooks-ts';
import { useLocation } from 'wouter';
import useUserStore from '../../../../../common/src/stores/useUserStore';
import { signOut } from 'common/src/api/auth';
import {
  useSectionItemsWithTeams,
  superadminItems,
  documentHelperItems,
} from './SidebarItems';
import DesktopSidebar from './DesktopSidebar';
import MobileSidebar from './MobileSidebar';
import { SidebarItem } from './SidebarTypes';
import SeasonWarningBanner from '../../SeasonWarningBanner';
import SettlementWarningsBanner from '../../SettlementWarningsBanner';
import DemoBanner from '../../DemoBanner';
import { SidebarIcon as Icon } from './SidebarIcon';
import { useDisclosure } from '@nextui-org/react';
import useFilterStore from '../../../../../common/src/stores/useFiltersStore';
import { CURRENT_SEASON } from 'common/src/utils/definitions';
import { useLanguageStore } from 'common/src/stores/useLanguageStore';
import useDataStore from '../../../../../common/src/stores/useDataStore';
import useShipmentsStore from '../../../store/useShipmentsStore';
import { getShipmentsAllSources } from '../../../services/shipmentsService';
import useSettlementsStore from '../../../store/useSettlementsStore';
import useFruticStore from '../../../store/useFruticStore';
import useWarningsStore from '../../../store/warningsStore';
import useExporterLabelsStore from '../../../store/useExporterLabelsStore';
import {
  getCustomCostCategoriesEnabled,
  getUnclassifiedExporterCosts,
} from 'common/src/api/exporter-cost-categories';
import AnnouncementModal from '../../../announcements/AnnouncementModal';
import TwoFactorModal from '../../auth/TwoFactorModal';

interface SidebarLayoutProps {
  children: ReactNode;
  /** Estado del factor TOTP (useMfaGate); null mientras no se sabe. */
  hasTwoFactor: boolean | null;
  onTwoFactorEnabled: () => void;
}

// Función auxiliar para filtrar elementos del sidebar según permisos
/**
 * Remove items that have an `allowedExporterIds` whitelist and the current
 * user's exporter is not in it. Admins and superadmins bypass the check.
 * Used for testing / gradual rollouts of sidebar entries.
 */
const filterItemsByExporterAllowlist = (
  items: SidebarItem[],
  userExporterId: number | null,
  isAdmin: boolean
): SidebarItem[] => {
  const passesAllowlist = (item: SidebarItem): boolean => {
    if (isAdmin) return true;
    if (!item.allowedExporterIds || item.allowedExporterIds.length === 0) {
      return true;
    }
    if (userExporterId == null) return false;
    return item.allowedExporterIds.includes(userExporterId);
  };

  const filterRecursive = (item: SidebarItem): SidebarItem | null => {
    if (!passesAllowlist(item)) return null;

    if (item.items && item.items.length > 0) {
      const filteredChildren = item.items
        .map(filterRecursive)
        .filter((child): child is SidebarItem => Boolean(child));

      if (filteredChildren.length === 0) return null;

      return {
        ...item,
        items: filteredChildren,
      };
    }

    return item;
  };

  return items
    .map(filterRecursive)
    .filter((item): item is SidebarItem => Boolean(item));
};

const filterItemsByPermissions = (
  items: SidebarItem[],
  permissions: Array<{ sidebarItemKey: string; enabled: boolean }>
) => {
  // Crear un mapa de permisos para acceso más eficiente
  const permissionsMap = new Map();
  permissions.forEach((p) => {
    // Almacenar tanto los permisos habilitados como deshabilitados
    permissionsMap.set(p.sidebarItemKey, p.enabled);
  });

  const filterRecursive = (item: SidebarItem): SidebarItem | null => {
    if (item.items && item.items.length > 0) {
      const filteredChildren = item.items
        .map(filterRecursive)
        .filter((child): child is SidebarItem => Boolean(child));

      if (filteredChildren.length === 0) return null;

      return {
        ...item,
        items: filteredChildren,
      };
    }

    return permissionsMap.get(item.permissionKey || item.key) === true
      ? item
      : null;
  };

  const filteredItems = items
    .map(filterRecursive)
    .filter((item): item is SidebarItem => Boolean(item));

  // Filtramos las secciones que quedaron sin ítems
  return filteredItems.filter(
    (section) => section.items && section.items.length > 0
  );
};

// Top bar component for updates
const UpdatesTopBar = ({ onClose }: { onClose: () => void }) => {
  const isMobile = useMediaQuery('(max-width: 768px)');

  return (
    <div
      className={`w-full bg-gradient-to-r from-blue-600 to-indigo-700 text-white py-3 px-4 ${
        isMobile
          ? 'flex flex-col items-start gap-3'
          : 'flex items-center justify-between'
      }`}
    >
      <div className='flex-1 pr-8'>
        <div className='flex items-start'>
          <svg
            xmlns='http://www.w3.org/2000/svg'
            className='h-5 w-5 mr-2 mt-0.5 flex-shrink-0'
            viewBox='0 0 20 20'
            fill='currentColor'
          >
            <path
              fillRule='evenodd'
              d='M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z'
              clipRule='evenodd'
            />
          </svg>
          <span className='font-medium'>
            Agregamos a los comparativos FOB industria, agrupar por Fecha de
            Zarpe y de Arribo, adicional a la Fecha de Venta.
          </span>
        </div>
      </div>

      {isMobile ? (
        <div className='w-full flex justify-center'>
          <button
            onClick={onClose}
            className='bg-background text-indigo-700 px-6 py-1.5 rounded-md text-sm font-medium hover:bg-default-200 focus:outline-none focus:ring-2 focus:ring-white'
          >
            Entendido
          </button>
        </div>
      ) : (
        <button
          onClick={onClose}
          className='text-white hover:text-default-300 focus:outline-none flex-shrink-0'
        >
          <svg
            xmlns='http://www.w3.org/2000/svg'
            className='h-5 w-5'
            viewBox='0 0 20 20'
            fill='currentColor'
          >
            <path
              fillRule='evenodd'
              d='M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z'
              clipRule='evenodd'
            />
          </svg>
        </button>
      )}
    </div>
  );
};

export default function SidebarLayout({
  children,
  hasTwoFactor,
  onTwoFactorEnabled,
}: SidebarLayoutProps) {
  const {
    userProfile,
    session,
    clearUserProfile,
    isSidebarCollapsed,
    setIsSidebarCollapsed,
  } = useUserStore();
  const { selectedSeason, selectedDivisionId, setSelectedDivisionId } =
    useFilterStore();
  // Roles without the sidebar filter must not keep a division persisted from
  // another account in this browser.
  const hasDivisionFilter = !(
    userProfile?.roles?.includes('superadmin') ||
    userProfile?.roles?.includes('document_helper')
  );
  useEffect(() => {
    if (!hasDivisionFilter && selectedDivisionId !== null)
      setSelectedDivisionId(null);
  }, [hasDivisionFilter, selectedDivisionId, setSelectedDivisionId]);
  const { selectedSpecies, species } = useDataStore();
  const { setShipments, setLoading, setError } = useShipmentsStore();
  const { fetchUniqueAttributes } = useSettlementsStore();
  const { fetchMappings } = useFruticStore();
  const { setWarnings, setUnclassifiedExporterCostsCount } = useWarningsStore();
  const { fetchLabels } = useExporterLabelsStore();
  const { language } = useLanguageStore();
  const [location, setLocation] = useLocation();
  const isMobile = useMediaQuery('(max-width: 768px)');

  const {
    isOpen: isMobileSidebarOpen,
    onOpen: onMobileSidebarOpen,
    onOpenChange: onMobileSidebarOpenChange,
  } = useDisclosure();

  const sectionItemsWithTeams = useSectionItemsWithTeams();

  // Check if season banner should show.
  // Contra CURRENT_SEASON y no contra la última de SEASONS: la temporada nueva
  // aparece en los selectores antes de tener datos cargados.
  const showSeasonBanner = selectedSeason && selectedSeason !== CURRENT_SEASON;

  const onToggle = React.useCallback(() => {
    setIsSidebarCollapsed(!isSidebarCollapsed);
  }, [isSidebarCollapsed, setIsSidebarCollapsed]);

  const onSelect = (key: string) => {
    // Determine which items set to search based on role
    const availableItems = userProfile?.roles?.includes('superadmin')
      ? superadminItems
      : userProfile?.roles?.includes('document_helper')
        ? documentHelperItems
        : filteredSectionItems;

    // Helper to search recursively in sections and items
    const findHref = (items: SidebarItem[]): string | undefined => {
      for (const section of items) {
        if (section.key === key && section.href) return section.href;
        if (section.items) {
          for (const subItem of section.items) {
            if (subItem.key === key && subItem.href) return subItem.href;
            if (subItem.items) {
              const found = findHref([subItem]);
              if (found) return found;
            }
          }
        }
      }
      return undefined;
    };

    const href = findHref(availableItems);
    if (href) setLocation(href);
  };

  const {
    isOpen: isTwoFactorOpen,
    onOpen: onOpenTwoFactor,
    onClose: onCloseTwoFactor,
  } = useDisclosure();
  // La opción solo se ofrece a quien no tiene la verificación activa; una vez
  // activa queda así (no se desactiva desde la app).

  const onLogout = () => {
    signOut();
    clearUserProfile();
  };

  // Alert: receiver costs without an exporter cost category.
  useEffect(() => {
    const exporterId = userProfile?.exporter_id;
    setUnclassifiedExporterCostsCount(0);
    if (!exporterId) return;
    let isMounted = true;
    const loadUnclassifiedCosts = async () => {
      try {
        if (!(await getCustomCostCategoriesEnabled(exporterId))) return;
        const unclassified = await getUnclassifiedExporterCosts(exporterId);
        if (isMounted) setUnclassifiedExporterCostsCount(unclassified.length);
      } catch (error) {
        console.error('Error loading unclassified exporter costs:', error);
      }
    };
    loadUnclassifiedCosts();
    window.addEventListener('sidebar-data-refresh', loadUnclassifiedCosts);
    return () => {
      isMounted = false;
      window.removeEventListener('sidebar-data-refresh', loadUnclassifiedCosts);
    };
  }, [setUnclassifiedExporterCostsCount, userProfile?.exporter_id]);

  useEffect(() => {
    let isMounted = true;

    const fetchSidebarData = async () => {
      if (
        !userProfile?.exporter_id ||
        !selectedSeason ||
        !selectedSpecies?.id
      ) {
        return;
      }
      const shouldLoadGlobalShipments =
        location !== '/shipments/details' && location !== '/shipments/summary';
      if (shouldLoadGlobalShipments) {
        setLoading(true);
        setError(null);
      }
      try {
        await Promise.all([
          shouldLoadGlobalShipments
            ? getShipmentsAllSources({
                userProfile,
                session,
                selectedSeason,
                selectedSpeciesId: selectedSpecies?.id,
              }).then((shipments) => {
                if (isMounted) {
                  setShipments(shipments);
                }
              })
            : Promise.resolve(),
          fetchUniqueAttributes({
            session,
            season: selectedSeason,
            speciesId: selectedSpecies?.id,
          }),
          fetchMappings(
            selectedSpecies?.id,
            selectedSeason,
            session,
            '',
            userProfile
          ),
          fetchLabels(
            userProfile.exporter_id,
            selectedSpecies.id,
            selectedSeason
          ),
        ]);

        const normalizeValue = (value: string | null | undefined) =>
          (value || '').trim().toUpperCase();

        const { mappedFormats, unmappedFormats } = useFruticStore.getState();
        const { uniqueLabels, uniqueVarieties } =
          useSettlementsStore.getState();

        const missingCodes = new Set<string>();
        unmappedFormats.forEach((code) => {
          const normalized = normalizeValue(code);
          if (normalized) missingCodes.add(normalized);
        });
        mappedFormats.forEach((mapping) => {
          const code = normalizeValue(mapping.frutic_code);
          const format = normalizeValue(mapping.cherry_box_format || '');
          const missingFormat = !format || format === 'NA';
          if (code && missingFormat) {
            missingCodes.add(code);
          }
        });

        const officialLabels = useExporterLabelsStore
          .getState()
          .labels.map((label) => normalizeValue(label.label))
          .filter((label) => label.length > 0);
        const officialLabelSet = new Set(officialLabels);

        const settlementLabelsMissingInMappings =
          officialLabelSet.size > 0
            ? Array.from(
                new Set(
                  uniqueLabels
                    .map((label) => normalizeValue(label))
                    .filter(
                      (label) =>
                        label.length > 0 && !officialLabelSet.has(label)
                    )
                )
              ).sort((a, b) => a.localeCompare(b))
            : [];

        const speciesWithVarieties =
          species.find((item) => item.id === selectedSpecies?.id) ||
          selectedSpecies;
        const officialVarieties = (speciesWithVarieties?.all_varieties || [])
          .map((variety) => normalizeValue(variety))
          .filter((variety) => variety.length > 0);

        console.log('officialVarieties', officialVarieties);
        const officialVarietySet = new Set(officialVarieties);
        const settlementVarietiesOutsideSpecies =
          officialVarieties.length > 0
            ? Array.from(
                new Set(
                  uniqueVarieties
                    .map((variety) => normalizeValue(variety))
                    .filter(
                      (variety) =>
                        variety.length > 0 && !officialVarietySet.has(variety)
                    )
                )
              ).sort((a, b) => a.localeCompare(b))
            : [];

        if (isMounted) {
          setWarnings({
            codesMissingMapping: Array.from(missingCodes).sort((a, b) =>
              a.localeCompare(b)
            ),
            settlementLabelsMissingInMappings,
            settlementVarietiesOutsideSpecies,
          });
        }
      } catch (error) {
        console.error('Error fetching sidebar data:', error);
        if (isMounted && shouldLoadGlobalShipments) {
          setError('Error fetching shipments');
        }
      } finally {
        if (isMounted && shouldLoadGlobalShipments) {
          setLoading(false);
        }
      }
    };
    fetchSidebarData();

    const handleSidebarRefresh = () => {
      fetchSidebarData();
    };

    window.addEventListener('sidebar-data-refresh', handleSidebarRefresh);

    return () => {
      isMounted = false;
      window.removeEventListener('sidebar-data-refresh', handleSidebarRefresh);
    };
  }, [
    selectedDivisionId,
    fetchMappings,
    fetchUniqueAttributes,
    selectedSeason,
    selectedSpecies?.id,
    location,
    // session,
    setError,
    setLoading,
    setShipments,
    setWarnings,
    userProfile,
    userProfile?.exporter_id,
  ]);

  // Filter out liquidation items if hasSettlements is false
  const filteredSectionItems = React.useMemo(() => {
    let finalItems = [];
    const hasSettlements = userProfile?.exporter?.hasSettlements;
    const hasFrutic = userProfile?.exporter?.hasFrutic;
    const hasDemo = userProfile?.exporter?.hasDemo;
    // Volumen Exportación: solo exportadoras suscritas a ASOEX (no preview).
    const hasVolume = userProfile?.exporter?.industry_volume_access === 'full';

    finalItems = sectionItemsWithTeams.map((section) => ({
      ...section,
      items: section.items
        ?.filter((item) => {
          if (item.key === 'liquidaciones') {
            return hasSettlements;
          }

          if (item.key === 'comparativos-fob') {
            return hasSettlements || hasVolume;
          }

          if (item.key === 'pre-settlements') {
            return hasSettlements;
          }

          if (item.key === 'settlements-control') {
            return hasSettlements && (hasFrutic || hasDemo);
          }
          return true;
        })
        // Dentro de Industria: Volumen con suscripción a ASOEX; lo demás, con liquidaciones.
        .map((item) =>
          item.key === 'comparativos-fob'
            ? {
                ...item,
                items: item.items?.filter((group) =>
                  group.key === 'industry-volume-group'
                    ? hasVolume
                    : hasSettlements
                ),
              }
            : item
        ),
    }));

    // Testing allowlist: hide items whose `allowedExporterIds` exclude the
    // current user's exporter. Admins / superadmins bypass this entirely.
    const userExporterId = userProfile?.exporter_id
      ? Number(userProfile.exporter_id)
      : null;
    const isAdmin = !!userProfile?.roles?.includes('superadmin');
    finalItems = filterItemsByExporterAllowlist(
      finalItems,
      userExporterId,
      isAdmin
    );

    // Si el usuario es "basic", filtrar según permisos
    if (userProfile?.roles?.includes('basic') && userProfile?.permissions) {
      finalItems = filterItemsByPermissions(
        finalItems,
        userProfile.permissions
      );
    }

    return finalItems;
  }, [
    sectionItemsWithTeams,
    userProfile?.exporter?.hasSettlements,
    userProfile?.exporter?.hasFrutic,
    userProfile?.exporter?.industry_volume_access,
    userProfile?.roles,
    userProfile?.permissions,
    userProfile?.exporter_id,
    language,
  ]);

  const itemsForSidebar = userProfile?.roles?.includes('superadmin')
    ? superadminItems
    : userProfile?.roles?.includes('document_helper')
      ? documentHelperItems
      : filteredSectionItems;

  // Burger button for mobile
  const burgerButton = (
    <button
      className='shadow-md bg-background p-2 rounded-lg'
      onClick={(e) => {
        e.stopPropagation();
        onMobileSidebarOpen();
      }}
    >
      <Icon
        className='text-default-500'
        height={24}
        icon='solar:hamburger-menu-outline'
        width={24}
      />
    </button>
  );

  return (
    <div className='flex flex-col h-dvh w-full bg-background text-foreground'>
      {/* {showUpdateBar && <UpdatesTopBar onClose={() => setShowUpdateBar(false)} />} */}
      <div className='flex flex-1 w-full'>
        {isMobile ? (
          <div className=''>
            <MobileSidebar
              location={location}
              sectionItemsWithTeams={itemsForSidebar}
              onSelect={onSelect}
              onLogout={onLogout}
              onOpenTwoFactor={
                hasTwoFactor === false
                  ? () => {
                      onMobileSidebarOpenChange();
                      onOpenTwoFactor();
                    }
                  : undefined
              }
              isOpen={isMobileSidebarOpen}
              onOpenChange={onMobileSidebarOpenChange}
              hideBurger={showSeasonBanner}
            />
          </div>
        ) : (
          <div className=''>
            <DesktopSidebar
              isCollapsed={isSidebarCollapsed}
              location={location}
              sectionItemsWithTeams={itemsForSidebar}
              onToggle={onToggle}
              onSelect={onSelect}
              onLogout={onLogout}
              onOpenTwoFactor={
                hasTwoFactor === false ? onOpenTwoFactor : undefined
              }
            />
          </div>
        )}
        <div className='w-full flex-1 flex-col overflow-auto'>
          <main className='h-full w-full overflow-visible'>
            <DemoBanner />
            <SeasonWarningBanner
              burgerButton={isMobile ? burgerButton : undefined}
            />
            {/* <SettlementWarningsBanner
              burgerButton={isMobile ? burgerButton : undefined}
            /> */}
            <div className='flex h-full w-full flex-col gap-4 rounded-medium'>
              {/* Division is a global scope like season: remount the page so it refetches. */}
              <div key={`division-${selectedDivisionId ?? 'all'}`}>
                {children}
              </div>
            </div>
          </main>
        </div>
      </div>
      <AnnouncementModal sidebarItems={itemsForSidebar} />
      <TwoFactorModal
        isOpen={isTwoFactorOpen}
        onClose={onCloseTwoFactor}
        onEnabled={onTwoFactorEnabled}
      />
    </div>
  );
}
